import './UserPreference.css';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SlCheckbox from '@shoelace-style/shoelace/dist/react/checkbox/index.js';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlSelect from '@shoelace-style/shoelace/dist/react/select/index.js';
import SlOption from '@shoelace-style/shoelace/dist/react/option/index.js';
import SlBadge from '@shoelace-style/shoelace/dist/react/badge/index.js';
import SlInput from '@shoelace-style/shoelace/dist/react/input/index.js';
import { CDSUiIcons, CDSIconSize, CDSIconWeight } from '@cencora-design-system/assets/icons';
import { useApi } from './useApi';
import EditSourceModal from './EditSourceModal';
import { useAuth } from './AuthContext';

// Manage Sources Tab Content
function ManageSourcesView() {
    const { authGet, authPut, authPost } = useApi();
    const [searchTerm, setSearchTerm] = useState('');
    const [sourcesData, setSourcesData] = useState([]);
    const [allSourcesData, setAllSourcesData] = useState([]);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedSource, setSelectedSource] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const [warningResolver, setWarningResolver] = useState(null);
    const [retentionCategoryOptions, setRetentionCategoryOptions] = useState([]);
    const [successMessage, setSuccessMessage] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [hasUserAgreedToWarning, setHasUserAgreedToWarning] = useState(false);

    const apiBaseUrl = process.env.REACT_APP_API_URL;

    // Format date to display format
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric', 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true 
        });
    };

    // Map API response to table format
    const mapSourceData = (sources) => {
        return sources.map((source, index) => {
            const descriptions = Array.isArray(source.sourceTitle) ? source.sourceTitle : [source.sourceTitle || '-'];
            const categories = Array.isArray(source.defaultCategory) ? source.defaultCategory : [source.defaultCategory || '-'];
            return {
                id: source.sourceId,
                srNo: index + 1,
                region: source.defaultRegion || '-',
                country: source.defaultCountry || '-',
                stateProvince: source.defaultState || '-',
                sourceName: source.sourceName || '-',
                url: source.baseUrl || '-',
                linkDescription: descriptions.filter(d => d && d !== '-').join(', ') || '-',
                category: categories.filter(c => c && c !== '-').join(', ') || '-',
                retentionCategory: source.policyType || '-',
                updatedBy: source.updated_by ? source.updated_by.split('@')[0].charAt(0).toUpperCase() + source.updated_by.split('@')[0].slice(1) : '-',
                updatedOn: source.updated_at,
                isEnabled: source.isActive,
                status: source.status || '-',
                //keep originals for editing
                allDescriptions: [...descriptions],
                allCategories: [...categories],
                rowIndex: 0
            };
        });    
    };

    // Fetch sources data from API
    const fetchSources = async () => {
        try {
            const response = await authGet(`${apiBaseUrl}/sources`);
            if (response.ok) {
                const data = await response.json();
                console.log("Sources API Response:", data);
                const mappedData = mapSourceData(data.sources);
                setSourcesData(mappedData);
                setAllSourcesData(mappedData);
                
            }
        } catch (error) {
            console.error("Error fetching sources:", error);
        }
    };

    

    async function fetchRetentionOptions(){
        const retentionPolicies = await authGet(`${apiBaseUrl}/retention-policies`);
        if (retentionPolicies.ok) {
            const data = await retentionPolicies.json();
            console.log("Retention policies API Response:", data);
            const uniquePolicyTypes = [...new Set(
                data.items
                    .map(policy => policy.policyType)
                    .filter(pt => pt && pt !== '-')
            )];
            const retentionOptions = uniquePolicyTypes.map(pt => ({ value: pt.replace(/\s+/g, '_'), label: pt }));
            console.log("Retention category options:", retentionOptions);
            setRetentionCategoryOptions(retentionOptions);
        }
    }

    useEffect(() => {
        fetchSources();
        fetchRetentionOptions();
    }, [authGet, apiBaseUrl]);

    useEffect(() => {
        console.log("isWarningOpen:", isWarningOpen);
    }, [isWarningOpen]);

    // Debounced search with AbortController (switchMap pattern - cancels previous requests)
    useEffect(() => {
        const abortController = new AbortController();

        const searchSources = async () => {
            if (!searchTerm.trim()) {
                // If search is empty, show all sources
                setSourcesData(allSourcesData);
                return;
            }

            setIsSearching(true);
            try {
                const response = await authGet(
                    `${apiBaseUrl}/sources/search?searchTerm=${encodeURIComponent(searchTerm)}`,
                    { signal: abortController.signal }
                );
                if (response.ok) {
                    const data = await response.json();
                    const mappedData = mapSourceData(data.sources || data);
                    setSourcesData(mappedData);
                }
            } catch (error) {
                // Ignore abort errors - they're expected when we cancel previous requests
                if (error.name !== 'AbortError') {
                    console.error("Error searching sources:", error);
                }
            } finally {
                if (!abortController.signal.aborted) {
                    setIsSearching(false);
                }
            }
        };

        const debounceTimer = setTimeout(searchSources, 300);
        
        // Cleanup: clear timeout and abort any pending request
        return () => {
            clearTimeout(debounceTimer);
            abortController.abort();
        };
    }, [searchTerm, authGet, apiBaseUrl, allSourcesData]);

    const openWarningModal = () => {
        setIsWarningOpen(true);

        return new Promise((resolve) => {
            setWarningResolver(() => resolve);
        });
    };
    

    const toggleEnabled = async (id) => {

        const source = sourcesData.find(s => s.id === id);
        if (!source) return;

        setSelectedSource(source);

        const confirmed = await openWarningModal();
        if (!confirmed) return;

        const newIsEnabled = !source.isEnabled;

        // Optimistic update
        setSourcesData(prevData => 
            prevData.map(s => 
                s.id === id 
                    ? { ...s, isEnabled: newIsEnabled }
                    : s
            )
        );

        try {
            const response = await authPut(`${apiBaseUrl}/sources/${id}`, { isActive: newIsEnabled });
            if (!response.ok) {
                // Rollback on failure
                setSourcesData(prevData => 
                    prevData.map(s => 
                        s.id === id 
                            ? { ...s, isEnabled: !newIsEnabled }
                            : s
                    )
                );
                console.error("Failed to update source status");
            }
        } catch (error) {
            // Rollback on error
            setSourcesData(prevData => 
                prevData.map(s => 
                    s.id === id 
                        ? { ...s, isEnabled: !newIsEnabled }
                        : s
                )
            );
            console.error("Error updating source status:", error);
        }
    };
const handleAddClick = () => {
    setIsAddModalOpen(true);
};

const handleAddModalClose = () => {
    setIsAddModalOpen(false);
};

const handleAddSubmit = async (newData) => {
    // Close modal and show success message
    setIsAddModalOpen(false);
    setSuccessMessage('Source added successfully');
    setTimeout(() => setSuccessMessage(null), 4000);
    console.log("Source added:", newData);
    
    // Refetch sources from backend to get latest data
    await fetchSources();
};
    const handleEditClick = (source) => {
        setSelectedSource(source);
        setIsEditModalOpen(true);
    };

    const handleEditModalClose = () => {
        setIsEditModalOpen(false);
        setSelectedSource(null);
    };

    const handleEditSubmit = async (updatedData) => {
        // Close modal and show success message
        setIsEditModalOpen(false);
        setSelectedSource(null);
        setSuccessMessage('Source updated successfully');
        setTimeout(() => setSuccessMessage(null), 4000);
        console.log("Source updated:", updatedData);
        
        // Refetch sources from backend to get latest data
        await fetchSources();
    };

    const handleWarningConfirm = () => {
        warningResolver?.(true);
        setIsWarningOpen(false);
    };

    const handleWarningCancel = () => {
        warningResolver?.(false);
        setIsWarningOpen(false);
    };
    return (
        <>
            {successMessage && (
                <div className="retention-success-message">
                    <SlBadge className="retention-success-badge" size="medium" pill>
                        {successMessage}
                    </SlBadge>
                </div>
            )}
                <EditSourceModal
                open={isEditModalOpen}
                onClose={handleEditModalClose}
                sourceData={selectedSource}
                onSubmit={handleEditSubmit}
                retentionCategoryOptions={retentionCategoryOptions}
                allSourcesData={sourcesData}
            />
            <EditSourceModal
                open={isAddModalOpen}
                onClose={handleAddModalClose}
                sourceData={null}
                onSubmit={handleAddSubmit}
                retentionCategoryOptions={retentionCategoryOptions}
                allSourcesData={sourcesData}
                isAddMode={true}
            />
            <EditSourceModal
                warningOpen={isWarningOpen}
                open={false}
                onClose={handleWarningCancel}
                sourceData={selectedSource}
                onSubmit={null}
                retentionCategoryOptions={[]}
                allSourcesData={sourcesData}
                isAddMode={false}
                isWarningMode={isWarningOpen}
                onConfirm={handleWarningConfirm}
                onCancel={handleWarningCancel}
            />
        <div className="manage-tab-content">
            <div className="form-sort-result-container">
                <div className="search-form">
                    <div className="search-input-group">
                        <SlInput
                            type="search"
                            clearable
                            className="search-box-managesource"
                            value={searchTerm}
                            onSlInput={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search"
                        />
                        <button className="search-button">
                            <SlIcon src="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxNiIgaGVpZ2h0PSIxNiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxjaXJjbGUgY3g9IjExIiBjeT0iMTEiIHI9IjgiLz48bGluZSB4MT0iMjEiIHkxPSIyMSIgeDI9IjE2LjY1IiB5Mj0iMTYuNjUiLz48L3N2Zz4=" />
                        </button>
                    </div>
                </div>
                <SlButton variant="primary" size="medium" onClick={handleAddClick}>
                    Add Source
                </SlButton>
            </div>

            <div className="table-container">
                <table className="manage-table sources-table">
                    <thead>
                        <tr>
                            <th>No.</th>
                            <th>Region</th>
                            <th>Country</th>
                            <th>State/Province</th>
                            <th>Source name</th>
                            <th>URL</th>
                            <th>Link description</th>
                            <th>Category</th>
                            <th>Retention category</th>
                            <th>Last updated by</th>
                            <th>Last updated on</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sourcesData.map((source, index) => (
                            <tr key={`${source.id}-${index}`}>
                                <td>{index + 1}</td>
                                <td title={source.region}>{source.region}</td>
                                <td title={source.country}>{source.country}</td>
                                <td title={source.stateProvince}>{source.stateProvince}</td>
                                <td title={source.sourceName}>{source.sourceName}</td>
                                <td title={source.url}>{source.url}</td>
                                <td title={source.linkDescription}>{source.linkDescription}</td>
                                <td title={source.category}>{source.category}</td>
                                <td title={source.retentionCategory}>{source.retentionCategory}</td>
                                <td title={source.updatedBy}>{source.updatedBy}</td>
                                <td title={formatDate(source.updatedOn)}>{formatDate(source.updatedOn)}</td>
                                <td title={source.status}>{source.status?.charAt(0).toUpperCase() + source.status?.slice(1)}</td>
                                <td>
                                    <div className="action-cell">
                                        <SlButton 
                                            variant="default"
                                            size="small"
                                            className="status-button"
                                            onClick={() => toggleEnabled(source.id)}
                                        >
                                            {source.isEnabled ? 'Disable' : 'Enable'}
                                        </SlButton>
                                        <button className="action-btn" title="Edit" onClick={() => handleEditClick(source)}>
                                            <SlIcon src={CDSUiIcons.IconPencil} size={CDSIconSize.size_lg} weight={CDSIconWeight.medium} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            
        </div>
        
        </>
    );
}

// Manage Retention Policy Tab Content
function ManageRetentionView({ authGet, authPut, userId }) {
    const [retentionData, setRetentionData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    const retentionPeriodOptions = [
        { value: '365', label: '365' },
        { value: '30', label: '30' },
        { value: '-1', label: 'Indefinite' }
    ];

    const apiBaseUrl = process.env.REACT_APP_API_URL;
    const retentionPoliciesApi = `${apiBaseUrl}/retention-policies`;

    useEffect(() => {
        fetchRetentionPolicies();
    }, []);

    async function fetchRetentionPolicies() {
        setIsLoading(true);
        setError(null);
        try {
            const response = await authGet(retentionPoliciesApi);
            if (response.ok) {
                const data = await response.json();
                console.log("Retention policies:", data);
                setRetentionData(Array.isArray(data) ? data : data.items || []);
            } else {
                console.error("Failed to fetch retention policies:", response.status);
                setError("Failed to load retention policies");
            }
        } catch (error) {
            console.error("Error fetching retention policies:", error);
            setError("Error loading retention policies");
        } finally {
            setIsLoading(false);
        }
    }

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const handleEditClick = (policyId) => {
        setEditingId(editingId === policyId ? null : policyId);
    };

    const handleRetentionChange = async (policyId, policyType, newValue) => {
        const ttlDays = parseInt(newValue);
        
        // Store original value for rollback on error
        const originalData = retentionData.find(p => p.id === policyId);
        const originalTtlDays = originalData?.ttlDays;
        
        // If same value selected, just close the dropdown without API call
        if (ttlDays === originalTtlDays) {
            setEditingId(null);
            return;
        }
        
        
        setEditingId(null);
        setSavingId(policyId);
        
        try {
            const response = await authPut(
                `${retentionPoliciesApi}/${policyType}`,
                { ttlDays: ttlDays }
            );
            
            if (response.ok) {
                console.log(`Retention policy ${policyType} updated successfully`);
                // Optimistic update - update UI immediately
                setRetentionData(prevData =>
                    prevData.map(policy =>
                        policy.id === policyId
                            ? { ...policy, ttlDays: ttlDays, modifiedAt: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }), createdBy: userId } 
                            : policy
                    )
                );
                setSuccessMessage('Retention policy updated');
                setTimeout(() => setSuccessMessage(null), 4000);
            } else {
                console.error(`Failed to update retention policy: ${response.status}`);
                setError(`Failed to update retention policy`);
                // Rollback on error
                setRetentionData(prevData =>
                    prevData.map(policy =>
                        policy.id === policyId
                            ? { ...policy, ttlDays: originalTtlDays }
                            : policy
                    )
                );
            }
        } catch (error) {
            console.error('Error updating retention policy:', error);
            setError('Error updating retention policy');
            // Rollback on error
            setRetentionData(prevData =>
                prevData.map(policy =>
                    policy.id === policyId
                        ? { ...policy, ttlDays: originalTtlDays }
                        : policy
                )
            );
        } finally {
            setSavingId(null);
        }
    };

    // Extract userID
    function getUserID(user) {
        const userData = user.split('@')[0];
        console.log(userData);
        return userData.charAt(0).toUpperCase() + userData.slice(1) || user;
    }

    if (isLoading) {
        return (
            <div className="manage-tab-content">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p className="loading-text">Loading retention policies...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="manage-tab-content">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    return (
        <div className="manage-tab-content">
            {successMessage && (
                <div className="retention-success-message">
                    <SlBadge className="retention-success-badge" size="medium" pill>
                        {successMessage}
                    </SlBadge>
                </div>
            )}

            <div className="table-container">
                <table className="manage-table retention-table">
                    <thead>
                        <tr>
                            <th>No.</th>
                            <th>Retention category</th>
                            <th>Retention period</th>
                            <th>Last updated by</th>
                            <th>Last updated on</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {retentionData.length > 0 ? (
                            retentionData.map((policy, index) => (
                                <tr key={policy.id || index}>
                                    <td>{index + 1}</td>
                                    <td>{policy.policyType}</td>
                                    <td>
                                        {editingId === policy.id ? (
                                            <SlSelect
                                                value={policy.ttlDays.toString()}
                                                onSlChange={(e) => handleRetentionChange(policy.id, policy.policyType, e.target.value)}
                                                onSlHide={() => setEditingId(null)}
                                                className="retention-period-select"
                                                size="small"
                                                placement="bottom"
                                                hoist
                                            >
                                                {retentionPeriodOptions.map(option => (
                                                    <SlOption key={option.value} value={option.value}>
                                                        {option.label}
                                                    </SlOption>
                                                ))}
                                            </SlSelect>
                                        ) : (
                                            <span className="retention-value-container">
                                                {policy.ttlDays === -1 ? 'Indefinite' : policy.ttlDays}
                                                {savingId === policy.id && <span className="saving-spinner"></span>}
                                            </span>
                                        )}
                                    </td>
                                    <td>{policy.createdBy.toLowerCase() === "system" ? "System" : getUserID(policy.createdBy)}</td>
                                    <td>{formatDate(policy.modifiedAt)}</td>
                                    <td>
                                        <button className="action-btn" title="Edit" onClick={() => handleEditClick(policy.id)}>
                                            <SlIcon src={CDSUiIcons.IconPencil} size={CDSIconSize.size_lg} weight={CDSIconWeight.medium} />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                    No retention policies found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function UserPreference(props) {
    const { authGet, authPost, authPut } = useApi();
    const { userRole, isAdmin } = useAuth();
    const navigate = useNavigate();
    const apiBaseUrl = process.env.REACT_APP_API_URL;
    const api = `${apiBaseUrl}/records`;
    
    // Use userId from props (from auth context)
    const userId = props.userId;
    const userPreferenceApi = `${apiBaseUrl}/userpreferences`;

    // Tab state - default to 'personalize' for non-admin users, 'sources' for admin
    const [activeTab, setActiveTab] = useState(isAdmin ? 'sources' : 'personalize');
    
    const [filtersData, setFiltersData] = useState({
        categories: [],
        sourceNames: [],
        regions: []
    });
    const [isLoading, setIsLoading] = useState(true);
    
    const [preferences, setPreferences] = useState({
        categories: [],
        sourceNames: [],
        regions: []
    });

    console.log("User role in UserPreference:", userRole);

    // Fetch initial filter data and user preferences
    useEffect(() => {
        fetchFilterData();
        fetchUserPreferences();
    }, []);

    // Fetch existing user preferences
    async function fetchUserPreferences() {
        try {
            const response = await authGet(`${userPreferenceApi}`);
            if (response.ok) {
                const data = await response.json();
                console.log("User preferences:", data);
                setPreferences({
                    categories: data.preferredCategories || [],
                    sourceNames: data.preferredSourcesNames || [],
                    regions: data.preferredRegions || []
                });
            } else {
                console.log("No existing preferences found for user");
            }
        } catch (error) {
            console.error("Error fetching user preferences:", error);
        }
    }

    async function fetchFilterData() {
        const payload = {
            "keyword": "",
            "applyFilters": false,
            "sortOrder": "DESC",
            "page": 1,
            "pageSize": 10
        };
        
        setIsLoading(true);
        try {
            const response = await authPost(api, payload);
            const data = await response.json();
            console.log("Filter data:", data.availableFilters);
            setFiltersData({
                categories: data.availableFilters?.categories || [],
                sourceNames: data.availableFilters?.sourceNames || [],
                regions: data.availableFilters?.regions || []
            });
        } catch (error) {
            console.error("Error fetching filter data:", error);
        } finally {
            setIsLoading(false);
        }
    }

    function handleCheckboxChange(section, value) {
        setPreferences(prev => {
            const current = prev[section] || [];
            const updated = current.includes(value) 
                ? current.filter(v => v !== value) 
                : [...current, value];
            return {
                ...prev,
                [section]: updated
            };
        });
    }

    async function handleSubmit() {
        const payload = {
            userId: userId,
            preferredRegions: preferences.regions,
            preferredCategories: preferences.categories,
            preferredSourcesNames: preferences.sourceNames
        };
        
        console.log("Submitting User Preferences:", payload);
        
        try {
            const response = await authPost(userPreferenceApi, payload);
            
            if (response.ok) {
                console.log("User preferences saved successfully");
                // Clear session storage filters so home page fetches fresh preferences
                sessionStorage.removeItem('selectedFilters');
                navigate('/');
            } else {
                console.error("Failed to save preferences:", response.status);
            }
        } catch (error) {
            console.error("Error saving preferences:", error);
        }
    }

    // Helper to render checkboxes in rows of 2
    function renderCheckboxRows(items, section) {
        const rows = [];
        for (let i = 0; i < items.length; i += 2) {
            rows.push(
                <div className="form-element" key={i}>
                    <div className="input-fields">
                        <SlCheckbox 
                            className="preference-checkbox"
                            checked={preferences[section].includes(items[i].value)}
                            onSlChange={() => handleCheckboxChange(section, items[i].value)}
                        >
                            {items[i].value}
                        </SlCheckbox>
                    </div>
                    {items[i + 1] && (
                        <div className="input-fields">
                            <SlCheckbox 
                                className="preference-checkbox"
                                checked={preferences[section].includes(items[i + 1].value)}
                                onSlChange={() => handleCheckboxChange(section, items[i + 1].value)}
                            >
                                {items[i + 1].value}
                            </SlCheckbox>
                        </div>
                    )}
                </div>
            );
        }
        return rows;
    }

    if (isLoading && activeTab === 'personalize') {
        return (
            <div className="user-preference-wrapper">
                <div className="user-preference-container">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p className="loading-text">Loading preferences...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Personalize Tab Content (Original UserPreference content)
    const renderPersonalizeContent = () => (
        <div className="user-preference-card">
            <div className="preference-header">
                <div className="preference-heading">
                    Select your preferences to receive personalized regulatory updates and insights
                </div>
                <div className="preference-separator"></div>
            </div>

            <div className="preference-content">
                <ul className="preference-listing">
                    {/* Category Section */}
                    <li>
                        <div className="listing-heading">Category</div>
                        {renderCheckboxRows(filtersData.categories, 'categories')}
                    </li>

                    {/* Region Section */}
                    <li>
                        <div className="listing-heading">Region</div>
                        {renderCheckboxRows(filtersData.regions, 'regions')}
                    </li>

                    {/* Source Section */}
                    <li>
                        <div className="listing-heading">Source</div>
                        {renderCheckboxRows(filtersData.sourceNames, 'sourceNames')}
                    </li>

                    {/* Submit */}
                    <li className="remove-li-border">
                        <div className="form-element">
                            <SlButton 
                                className="button-submit" 
                                variant="primary"
                                onClick={handleSubmit}
                            >
                                Submit
                            </SlButton>
                        </div>
                    </li>
                </ul>
            </div>
        </div>
    );

    return (
        <div className="user-preference-wrapper">
            <div className="user-preference-container">
                <div className="back-to-dashboard" onClick={() => navigate('/')}>
                    <SlIcon src="data:image/svg+xml;base64,PHN2ZyB2aWV3Qm94PSIwIDAgMjQgMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSJjdXJyZW50Q29sb3IiIHN0cm9rZS13aWR0aD0iMS41IiBmaWxsPSJub25lIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPjxwYXRoIGQ9Ik0yMCAxMkg0bTAgMCA3LTdtLTcgNyA3IDciLz48L3N2Zz4=" />
                    <span>Back to Dashboard</span>
                </div>

                {/* Tab Navigation */}
                
                    {isAdmin && (
                        <ul className="manage-tabs">
                            <li>
                                <button 
                                    className={`tab-btn ${activeTab === 'sources' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('sources')}
                                >
                                    <SlIcon src={CDSUiIcons.IconListBullets} size={CDSIconSize.size_xl} weight={CDSIconWeight.medium} />
                                    <span>Manage sources</span>
                                </button>
                            </li>
                            <li>
                                <button 
                                    className={`tab-btn ${activeTab === 'keywords' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('keywords')}
                                >
                                    <SlIcon src={CDSUiIcons.IconTimeClockCircle} size={CDSIconSize.size_xl} weight={CDSIconWeight.medium} />
                                    <span>Manage retention policy</span>
                                </button>
                            </li>
                            <li>
                        <button 
                            className={`tab-btn ${activeTab === 'personalize' ? 'active' : ''}`}
                            onClick={() => setActiveTab('personalize')}
                        >
                            <SlIcon src={CDSUiIcons.IconPencil} size={CDSIconSize.size_xl} weight={CDSIconWeight.medium} />
                            <span>Personalize</span>
                        </button>
                    </li>
                </ul>
                        
                    )}
                    

                {/* Tab Content */}
                {isAdmin && activeTab === 'sources' && <ManageSourcesView />}
                {isAdmin && activeTab === 'keywords' && <ManageRetentionView userId={userId} authGet={authGet} authPut={authPut} />}
                {activeTab === 'personalize' && renderPersonalizeContent()}
            </div>
        </div>
    );
}
